import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CheckCheck, Eye, FileDown } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError, EstadoVacio } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { type ArchivoParaVer, VistaPreviaArchivo } from "@/componentes/ui/VistaPreviaArchivo"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha, comoFechaHora } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

import { cifra, descargarRecibo } from "./descarga"
import { type Documento, ESTADOS, MONEDAS, numero, type Recibo, TONO_ESTADO } from "./tipos"

const BASE = "/api/v1/agencia-transporte"

const ETIQUETA_FILTRO: Record<string, string> = {
  EMITIDO: "Por recibir",
  RECIBIDO: "Recibidos",
  ANULADO: "Anulados",
  "": "Todos",
}

/**
 * ⚠ **Se arma con las filas y no leyendo la tabla**: el total mezcla bolívares y
 * divisa, y leída del DOM esa columna saldría como texto. Aquí va la cifra como número
 * y la moneda en su propia columna.
 */
function documentoDeRecibos(recibos: Recibo[], estado: string): DocumentoExportable {
  return {
    titulo: "Recibos de flete",
    subtitulo: `Estado: ${ETIQUETA_FILTRO[estado] ?? estado} · ${recibos.length} recibos`,
    secciones: [
      {
        titulo: "Recibos de flete",
        columnas: [
          { titulo: "Nº", tipo: "texto" },
          { titulo: "Fecha", tipo: "fecha" },
          { titulo: "Intermediario", tipo: "texto" },
          { titulo: "Empresa de transporte", tipo: "texto" },
          { titulo: "Expediente / BL", tipo: "texto" },
          { titulo: "Total", tipo: "monto" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Estado", tipo: "texto" },
        ],
        filas: recibos.map((r) => ({
          celdas: [
            numero(r.numero),
            r.fecha.slice(0, 10),
            r.snapshot?.intermediario.nombre ?? null,
            r.snapshot?.empresa.razon_social ?? null,
            [r.expediente_numero, r.bl].filter(Boolean).join(" · ") || null,
            r.total === null ? null : Number(r.total),
            MONEDAS[r.moneda],
            ESTADOS[r.estado],
          ],
        })),
      },
    ],
  }
}

/** Los recibos emitidos en el portal de transporte, vistos por administración. */
export function AgenciaRecibos() {
  const [estado, setEstado] = useState("EMITIDO")
  const [abierto, setAbierto] = useState<Recibo | null>(null)
  const recibos = useQuery({
    queryKey: ["agencia-transporte", "recibos", estado],
    queryFn: () => api.get<Recibo[]>(`${BASE}/recibos${estado ? `?estado=${estado}` : ""}`),
  })

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <label className="grid w-fit gap-1 text-sm">
          Estado
          <select className="campo" value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="EMITIDO">Por recibir</option>
            <option value="RECIBIDO">Recibidos</option>
            <option value="ANULADO">Anulados</option>
            <option value="">Todos</option>
          </select>
        </label>
        {!!recibos.data?.length && (
          <Exportar
            titulo="Recibos de flete"
            documento={() => documentoDeRecibos(recibos.data ?? [], estado)}
          />
        )}
      </div>
      {recibos.isPending && <EstadoCarga etiqueta="Cargando recibos" />}
      {recibos.isError && (
        <EstadoError
          titulo="No se pudieron cargar"
          error={recibos.error}
          onReintentar={() => recibos.refetch()}
        />
      )}
      {recibos.data?.length === 0 && (
        <EstadoVacio
          titulo="Sin recibos"
          texto="Aquí aparecen los recibos que emiten los intermediarios de transporte."
        />
      )}
      {!!recibos.data?.length && (
        <Tabla etiqueta="Recibos de flete">
          <thead>
            <tr>
              <Th>Nº</Th>
              <Th>Fecha</Th>
              <Th>Intermediario</Th>
              <Th>Empresa de transporte</Th>
              <Th>Expediente / BL</Th>
              <Th className="text-right">Total</Th>
              <Th>Estado</Th>
            </tr>
          </thead>
          <tbody>
            {recibos.data.map((r) => (
              <tr key={r.id}>
                <Td className="tabular-nums">
                  <button
                    type="button"
                    className="underline-offset-2 hover:underline"
                    onClick={() => setAbierto(r)}
                  >
                    {numero(r.numero)}
                  </button>
                </Td>
                <Td className="tabular-nums">{comoFecha(r.fecha)}</Td>
                <Td>{r.snapshot?.intermediario.nombre ?? "—"}</Td>
                <Td>{r.snapshot?.empresa.razon_social ?? "—"}</Td>
                <Td>{[r.expediente_numero, r.bl].filter(Boolean).join(" · ") || "—"}</Td>
                <Td className="text-right tabular-nums">
                  {cifra(r.total)} {MONEDAS[r.moneda]}
                </Td>
                <Td>
                  <Insignia tono={TONO_ESTADO[r.estado]}>{ESTADOS[r.estado]}</Insignia>
                </Td>
              </tr>
            ))}
          </tbody>
        </Tabla>
      )}
      {abierto && <Detalle recibo={abierto} onCerrar={() => setAbierto(null)} />}
    </div>
  )
}

function Detalle({ recibo, onCerrar }: { recibo: Recibo; onCerrar: () => void }) {
  const cliente = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const [r, setR] = useState(recibo)
  const [viendo, setViendo] = useState<ArchivoParaVer | null>(null)
  const docs = useQuery({
    queryKey: ["agencia-transporte", "documentos", r.id],
    queryFn: () => api.get<Documento[]>(`${BASE}/recibos/${r.id}/documentos`),
  })
  const recibir = useMutation({
    mutationFn: () => api.post<Recibo>(`${BASE}/recibos/${r.id}/recibir`),
    onSuccess: (nuevo) => {
      setR(nuevo)
      cliente.invalidateQueries({ queryKey: ["agencia-transporte", "recibos"] })
    },
  })
  const pdf = useMutation({ mutationFn: () => descargarRecibo(r, BASE) })
  const f = r.snapshot
  const error = recibir.error ?? pdf.error

  return (
    <Modal abierto={true} titulo={`Recibo Nº ${numero(r.numero)}`} onCerrar={onCerrar} ancho="lg">
      <div className="space-y-4 text-sm">
        <p className="flex flex-wrap items-center gap-2">
          <Insignia tono={TONO_ESTADO[r.estado]}>{ESTADOS[r.estado]}</Insignia>
          <span className="tabular-nums">
            {cifra(r.total)} {MONEDAS[r.moneda]}
          </span>
          {r.recibido_en && (
            <span style={{ color: "var(--fg-muted)" }}>
              Recibido el {comoFechaHora(r.recibido_en)}
            </span>
          )}
          {r.motivo_anulacion && (
            <span style={{ color: "var(--fg-muted)" }}>Anulado: {r.motivo_anulacion}</span>
          )}
        </p>
        {f && (
          <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
            <Dato t="Empresa" v={`${f.empresa.razon_social} · ${f.empresa.rif}`} />
            <Dato
              t="Intermediario"
              v={[f.intermediario.nombre, f.intermediario.documento, f.intermediario.telefono]
                .filter(Boolean)
                .join(" · ")}
            />
            <Dato
              t="Vehículo"
              v={[f.vehiculo.placa, f.vehiculo.placa_remolque, f.vehiculo.marca]
                .filter(Boolean)
                .join(" · ")}
            />
            <Dato t="Chofer" v={`${f.chofer.nombre} · C.I. ${f.chofer.cedula}`} />
            <Dato
              t="Ruta"
              v={[f.servicio.origen, f.servicio.destino].filter(Boolean).join(" → ")}
            />
            <Dato
              t="Expediente / BL"
              v={[f.servicio.expediente, f.servicio.bl].filter(Boolean).join(" · ")}
            />
          </dl>
        )}
        {f && (
          <ul className="space-y-1">
            {f.lineas.map((l, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: renglones congelados, sin id
              <li key={i} className="flex justify-between gap-2 tabular-nums">
                <span>
                  {l.cantidad} × {l.descripcion} {l.contenedor ? `· ${l.contenedor}` : ""}
                </span>
                <span>{cifra(l.importe)}</span>
              </li>
            ))}
          </ul>
        )}
        <section>
          <h3 className="mb-1 font-semibold">Soportes (servicio, chofer y vehículo)</h3>
          {docs.isPending && <p style={{ color: "var(--fg-muted)" }}>Cargando…</p>}
          <ul className="space-y-1">
            {docs.data?.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  className="flex items-center gap-1 underline-offset-2 hover:underline"
                  onClick={() =>
                    setViendo({
                      ruta: `${BASE}/documentos/${d.id}/archivo`,
                      nombre: d.nombre_original,
                    })
                  }
                >
                  <Eye size={12} aria-hidden={true} /> {d.dueno.toLowerCase()} · {d.tipo} ·{" "}
                  {d.nombre_original}
                </button>
              </li>
            ))}
          </ul>
        </section>
        {error && (
          <p role="alert" style={{ color: "var(--destructive)" }}>
            {error instanceof ErrorApi ? error.message : "No se pudo completar"}
          </p>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <Boton disabled={pdf.isPending || !f} onClick={() => pdf.mutate()}>
            <FileDown size={16} aria-hidden={true} /> Descargar PDF
          </Boton>
          {r.estado === "EMITIDO" && tienePermiso(usuario, "transporte:recibir") && (
            <Boton
              variante="primario"
              disabled={recibir.isPending}
              onClick={() => recibir.mutate()}
            >
              <CheckCheck size={16} aria-hidden={true} /> Marcar recibido en administración
            </Boton>
          )}
        </div>
      </div>
      <VistaPreviaArchivo archivo={viendo} onCerrar={() => setViendo(null)} />
    </Modal>
  )
}

function Dato({ t, v }: { t: string; v: string }) {
  return (
    <div>
      <dt className="text-xs" style={{ color: "var(--fg-muted)" }}>
        {t}
      </dt>
      <dd>{v || "—"}</dd>
    </div>
  )
}
